import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Package, Layers, Box, CheckCircle2 } from '@/lib/icons';

import { getDeviceModels } from '@/features/device-models/api';
import { deviceCatalogKeys } from '@/features/device-models/keys';
import type { DeviceCategory } from '@/features/device-models/device-model';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useAuth } from '@/hooks/useAuth';

export const Route = createFileRoute('/_layout/device-models/')({
  component: DeviceModelsPage,
});

const categoryBadge = (category: string) => {
  const map: Record<string, { cls: string; label: string }> = {
    phone: {
      cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
      label: 'Phone',
    },
    tablet: {
      cls: 'bg-violet-500/15 text-violet-600 border-violet-500/30 dark:text-violet-400',
      label: 'Tablet',
    },
    laptop: {
      cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
      label: 'Laptop',
    },
    accessory: {
      cls: 'bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400',
      label: 'Accessory',
    },
  };
  const s = map[category] ?? { cls: '', label: category };
  return <Badge className={s.cls}>{s.label}</Badge>;
};

const PAGE_SIZE = 20;

function DeviceModelsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Catalogue configuration is admin-only — see lib/auth/admin-scopes.
  // The API refuses the write regardless; this keeps the portal from
  // offering a form that cannot be saved.
  const canCreate = useAuth().hasPermission('device_models', 'create');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<
    DeviceCategory | undefined
  >();
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: deviceCatalogKeys.modelList({
      search: search || undefined,
      category: categoryFilter,
    }),
    queryFn: () =>
      getDeviceModels({
        search: search || undefined,
        category: categoryFilter,
      }),
  });

  const allModels = data?.value ?? [];
  const totalPages = Math.max(1, Math.ceil(allModels.length / PAGE_SIZE));
  const models = allModels.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const startItem = allModels.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const endItem = Math.min(page * PAGE_SIZE, allModels.length);

  const totalModels = allModels.length;
  const activeModels = allModels.filter((m) => m.isActive).length;
  const totalVariants = allModels.reduce((sum, m) => sum + m.variantCount, 0);
  const totalStock = allModels.reduce((sum, m) => sum + m.totalStock, 0);

  const stats = [
    {
      label: t('deviceModels.stats.totalModels'),
      value: totalModels,
      icon: Package,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: t('deviceModels.stats.activeModels'),
      value: activeModels,
      icon: CheckCircle2,
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      label: t('deviceModels.stats.totalVariants'),
      value: totalVariants,
      icon: Layers,
      color: 'from-violet-500 to-violet-600',
    },
    {
      label: t('deviceModels.stats.totalStock'),
      value: totalStock,
      icon: Box,
      color: 'from-amber-500 to-amber-600',
    },
  ];

  const categories: { label: string; value: DeviceCategory | undefined }[] = [
    { label: t('common.allStatuses'), value: undefined },
    { label: t('deviceModels.categories.phone'), value: 'phone' },
    { label: t('deviceModels.categories.tablet'), value: 'tablet' },
    { label: t('deviceModels.categories.laptop'), value: 'laptop' },
    { label: t('deviceModels.categories.accessory'), value: 'accessory' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('deviceModels.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('deviceModels.description')}
          </p>
        </div>
        {canCreate && (
          <Button
            className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200"
            onClick={() => navigate({ to: '/device-models/new' })}
          >
            <Plus className="w-4 h-4 mr-2" /> {t('deviceModels.newModel')}
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {isLoading ? '—' : s.value}
                </p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-lg mr-2">
                {t('deviceModels.models')}
              </CardTitle>
              {categories.map((c) => (
                <Button
                  key={c.label}
                  variant={categoryFilter === c.value ? 'default' : 'outline'}
                  size="sm"
                  className={
                    categoryFilter === c.value
                      ? 'bg-primary text-primary-foreground'
                      : ''
                  }
                  onClick={() => {
                    setCategoryFilter(c.value);
                    setPage(1);
                  }}
                >
                  {c.label}
                </Button>
              ))}
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('deviceModels.search')}
                className="pl-9"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6">
              <ApiErrorCard
                error={error}
                onRetry={() => refetch()}
                title="Failed to load device models"
              />
            </div>
          ) : allModels.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">{t('deviceModels.empty.title')}</p>
              <p className="text-sm">{t('deviceModels.empty.description')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>{t('deviceModels.columns.brand')}</TableHead>
                    <TableHead>{t('deviceModels.columns.name')}</TableHead>
                    <TableHead>{t('deviceModels.columns.category')}</TableHead>
                    <TableHead>{t('deviceModels.columns.variants')}</TableHead>
                    <TableHead>
                      {t('deviceModels.columns.totalStock')}
                    </TableHead>
                    <TableHead>{t('deviceModels.columns.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {models.map((m) => (
                    <TableRow
                      key={m.id}
                      className="hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() =>
                        navigate({
                          to: '/device-models/$modelId',
                          params: { modelId: m.id },
                        })
                      }
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center">
                            <Package className="w-4 h-4 text-slate-500 dark:text-slate-300" />
                          </div>
                          <span className="font-medium">{m.brand}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell>{categoryBadge(m.category)}</TableCell>
                      <TableCell>{m.variantCount}</TableCell>
                      <TableCell>{m.totalStock}</TableCell>
                      <TableCell>
                        {m.isActive ? (
                          <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
                            {t('common.status.active')}
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400">
                            {t('common.status.inactive')}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {allModels.length > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {startItem}-{endItem} of {allModels.length}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={
                    page <= 1
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
              {Array.from({ length: totalPages }).map((_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    isActive={page === i + 1}
                    onClick={() => setPage(i + 1)}
                    className="cursor-pointer"
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={
                    page >= totalPages
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
